import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DicomMetadataStore, MODULE_TYPES, useSystem } from '@ohif/core';

import Dropzone from 'react-dropzone';
import filesToStudies from './filesToStudies';

import { extensionManager } from '../../App';
import { Button, Icons } from '@ohif/ui-next';

/**
 * URL → File adapter
 */
async function fileFromUrl(url: string): Promise<File> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch URL: ${response.status}`);
  }

  const blob = await response.blob();
  const fileName = url.split('/').pop() || 'remote.dcm';

  return new File([blob], fileName, {
    type: blob.type || 'application/dicom',
  });
}

const getLoadButton = (onDrop, text, isDir) => (
  <Dropzone onDrop={onDrop} noDrag>
    {({ getRootProps, getInputProps }) => (
      <div {...getRootProps()}>
        <Button variant="default" className="w-28">
          {text}
          {isDir ? (
            <input
              {...getInputProps()}
              webkitdirectory="true"
              mozdirectory="true"
              style={{ display: 'none' }}
            />
          ) : (
            <input {...getInputProps()} style={{ display: 'none' }} />
          )}
        </Button>
      </div>
    )}
  </Dropzone>
);

type LocalProps = {
  modePath: string;
};

function Local({ modePath }: LocalProps) {
  const { servicesManager } = useSystem();
  const { customizationService } = servicesManager.services;
  const navigate = useNavigate();
  const dropzoneRef = useRef<any>(null);
  const autoLoadRef = useRef(false);

  const [dropInitiated, setDropInitiated] = useState(false);
  const [fileUrl, setFileUrl] = useState('');
  const [loadingFromUrl, setLoadingFromUrl] = useState(false);

  const LoadingIndicatorProgress = customizationService.getCustomization(
    'ui.loadingIndicatorProgress'
  );

  /**
   * Initialize dicomlocal datasource
   */
  const dataSourceModules = extensionManager.modules[MODULE_TYPES.DATA_SOURCE];
  const localDataSources = dataSourceModules.flatMap(ds =>
    ds.module.filter(m => m.type === 'localApi')
  );
  const dataSource = localDataSources[0].createDataSource({});

  const microscopyExtensionLoaded =
    extensionManager.registeredExtensionIds.includes(
      '@ohif/extension-dicom-microscopy'
    );

  /**
   * Core ingestion pipeline
   */
  const onDrop = async acceptedFiles => {
    const studies = await filesToStudies(acceptedFiles, dataSource);
    const query = new URLSearchParams();

    if (microscopyExtensionLoaded) {
      const smStudies = studies.filter(id => {
        const study = DicomMetadataStore.getStudy(id);
        return study.series.some(
          s => s.Modality === 'SM' || s.instances[0]?.Modality === 'SM'
        );
      });

      if (smStudies.length) {
        smStudies.forEach(id => query.append('StudyInstanceUIDs', id));
        modePath = 'microscopy';
      }
    }

    studies.forEach(id => query.append('StudyInstanceUIDs', id));
    query.append('datasources', 'dicomlocal');

    navigate(`/${modePath}?${decodeURIComponent(query.toString())}`);
  };

  /**
   * URL-driven ingestion
   */
  const onLoadFromUrl = async (urlOverride?: string) => {
    try {
      const targetUrl = urlOverride || fileUrl;
      if (!targetUrl) return;

      setDropInitiated(true);
      setLoadingFromUrl(true);

      const file = await fileFromUrl(targetUrl);
      await onDrop([file]);
    } catch (err) {
      console.error(err);
      alert('Failed to load DICOM from URL');
      setDropInitiated(false);
    } finally {
      setLoadingFromUrl(false);
    }
  };

  /**
   * Auto-load via ?url=
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');

    if (urlParam && !autoLoadRef.current) {
      autoLoadRef.current = true;
      setFileUrl(urlParam);
      onLoadFromUrl(urlParam);
    }
  }, []);

  /**
   * UI styling
   */
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => document.body.classList.remove('bg-black');
  }, []);

  return (
    <Dropzone
      ref={dropzoneRef}
      onDrop={files => {
        setDropInitiated(true);
        onDrop(files);
      }}
      noClick
    >
      {({ getRootProps }) => (
        <div {...getRootProps()} style={{ width: '100%', height: '100%' }}>
          <div className="flex h-screen w-screen items-center justify-center">
            <div className="bg-muted border-primary/60 mx-auto space-y-4 rounded-xl border border-dashed py-12 px-12 drop-shadow-md">
              <div className="flex justify-center">
                <Icons.OHIFLogoColorDarkBackground className="h-18" />
              </div>

              <div className="text-center space-y-2">
                {dropInitiated ? (
                  <LoadingIndicatorProgress className="h-full w-full bg-black" />
                ) : (
                  <>
                    <p className="text-primary text-xl">
                      Drag & drop DICOM files or folders
                    </p>
                    <p className="text-muted-foreground text-base">
                      Data remains local within your browser
                    </p>
                  </>
                )}
              </div>

              <div className="flex justify-center gap-2 pt-4">
                {getLoadButton(onDrop, 'Load files', false)}
                {getLoadButton(onDrop, 'Load folders', true)}
              </div>

              {/* URL ingestion */}
              <div className="flex flex-col gap-2 pt-4">
                <input
                  type="text"
                  placeholder="Paste DICOM file URL"
                  value={fileUrl}
                  onChange={e => setFileUrl(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                />
                <Button
                  variant="default"
                  disabled={!fileUrl || loadingFromUrl}
                  onClick={() => onLoadFromUrl()}
                >
                  Load from URL
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dropzone>
  );
}

export default Local;
